import { useCallback, useRef, useState } from "react";
import { encodeFunctionData, getAddress } from "viem";
import { nftSchema, type BootstrapResponse } from "../../../api/schemas";
import { ponsRewardNftAbi } from "../../../contracts/pons-reward-nft";
import { parseUnsignedDecimal } from "../../../lib/financial";
import { FinalizationNotNeeded, readNftState } from "../../../wallet/nft-lifecycle";
import type { WalletSessionController } from "../../../wallet/wallet-session";
import type { TransactionTracker } from "../../../wallet/transaction-tracker";
import { ContextChangedError } from "../../../wallet/wallet-operation";
import type { ActionContext } from "../../../state/action-context";
import { sameAddress } from "../../../state/model-helpers";
import { actionPermissions } from "../../../state/transaction-capability";

export function useFinalizeNft({ walletController, bootstrap, tracker, transactionEnvironment, refresh,
  contextKey, readContextKey, beginOperation, endOperation, activeContext }: ActionContext & {
  walletController: WalletSessionController; bootstrap: BootstrapResponse | null;
  tracker: TransactionTracker; transactionEnvironment: string; refresh(): void;
}) {
  const pending = useRef(false);
  const [notice, setNotice] = useState<{ context: string; message: string } | null>(null);
  const finalizeNft = useCallback(async (tokenId: string) => {
    if (contextKey !== readContextKey()) throw new ContextChangedError();
    if (pending.current) throw new Error("Finalization is already in progress.");
    const permission = actionPermissions(bootstrap).finalize;
    if (permission.state !== "ALLOWED") throw new Error(permission.reasons.join(", "));
    const collection = bootstrap?.contracts.collectionAddress;
    const chainId = bootstrap?.contracts.mintChainId;
    const sourceToken = activeContext.current.token;
    const account = walletController.getAccount();
    const provider = walletController.getProvider();
    if (!collection || !chainId || !sourceToken || !account || !provider) throw new Error("Connect a wallet and select the configured collection/source token.");
    const id = parseUnsignedDecimal(tokenId, "token ID");
    if (id >= 1n << 256n || id.toString() !== tokenId) throw new Error("Invalid NFT token ID");
    if (tracker.getSnapshot().items.some((item) => item.kind === "FINALIZE" && item.tokenId === tokenId &&
      item.chainId === chainId && sameAddress(item.collection, collection) && item.phase === "SAMPLING_EXHAUSTED")) {
      throw new Error("Sampling counter exhausted. Operator review is required.");
    }
    const operation = beginOperation();
    pending.current = true;
    setNotice(null);
    try {
      await tracker.execute({ kind: "FINALIZE", account, chainId, collection, sourceToken, tokenId,
        environment: transactionEnvironment }, provider, async () => {
        const detail = await walletController.api.get(`/nfts/${tokenId}`, nftSchema, operation.signal);
        operation.assertCurrent();
        if (detail.tokenId !== tokenId || detail.chainId !== chainId || !sameAddress(detail.collectionAddress, collection) ||
          !sameAddress(detail.sourceToken, sourceToken)) throw new Error("NFT detail does not match the selected token and deployment.");
        // Finalization is permissionless: neither inventory membership nor ownership is a prerequisite.
        await operation.switchTo(chainId);
        const state = await readNftState(provider, { chainId, collection: getAddress(collection), tokenId: id },
          operation.assertCurrent, { signal: operation.signal });
        if (!sameAddress(state.sourceToken, sourceToken)) throw new Error("NFT source token does not match the selected source.");
        if (state.status === 3 || state.status === 4) throw new FinalizationNotNeeded();
        if (state.status !== 2) throw new Error("Randomness is not ready. Wait for provider delivery before finalizing.");
        return { account: getAddress(account), chainId, to: getAddress(collection), valueWei: 0n,
          data: encodeFunctionData({ abi: ponsRewardNftAbi, functionName: "finalizeReward", args: [id] }) };
      }, operation);
      if (contextKey === readContextKey()) {
        setNotice({ context: contextKey, message: "Finalization check finished. See transaction activity for its result; NFT data refreshes separately." });
        refresh();
      }
    } finally { pending.current = false; endOperation(operation); }
  }, [walletController, bootstrap, tracker, transactionEnvironment, refresh, contextKey, readContextKey, beginOperation, endOperation, activeContext]);
  return { finalizeNft, finalizeNotice: notice?.context === contextKey ? notice.message : null };
}
